import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { QualifyHost } from "@/components/crm/QualifyHost";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth, RequirePermission } from "@/components/layout/Guards";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import SignPage from "@/pages/SignPage";
import WebFormPage from "@/pages/WebFormPage";
import TechFormPage from "@/pages/TechFormPage";
import TechSubmissionsPage from "@/pages/TechSubmissionsPage";
import PlatformTenants from "@/pages/admin/PlatformTenants";
import Dashboard from "@/pages/Dashboard";
import PlaceholderModule from "@/pages/PlaceholderModule";
import NotFound from "@/pages/NotFound";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminAudit from "@/pages/admin/AdminAudit";
import AutomationsPage from "@/pages/admin/AutomationsPage";
import CalendarPage from "@/pages/CalendarPage";
import DemoLogin from "@/pages/DemoLogin";
import CompaniesList from "@/pages/crm/CompaniesList";
import CompanyDetail from "@/pages/crm/CompanyDetail";
import LeadsList from "@/pages/crm/LeadsList";
import ActivitiesPage from "@/pages/ActivitiesPage";
import OpportunitiesPage from "@/pages/sales/OpportunitiesPage";
import OpportunityDetail from "@/pages/sales/OpportunityDetail";
import QuotesList from "@/pages/sales/QuotesList";
import QuoteDetail from "@/pages/sales/QuoteDetail";
import CatalogPage from "@/pages/sales/CatalogPage";
import ForecastPage from "@/pages/sales/ForecastPage";
import FollowupPage from "@/pages/sales/FollowupPage";
import AdminPipeline from "@/pages/admin/AdminPipeline";
import InterventionsPage from "@/pages/interventions/InterventionsPage";
import InterventionDetail from "@/pages/interventions/InterventionDetail";
import ReportsPage from "@/pages/reports/ReportsPage";
import ReportDetail from "@/pages/reports/ReportDetail";
import RateCardsPage from "@/pages/admin/RateCardsPage";
import AssetCategoriesPage from "@/pages/admin/AssetCategoriesPage";
import AssetsPage from "@/pages/assets/AssetsPage";
import AssetDetail from "@/pages/assets/AssetDetail";
import OrdersPage from "@/pages/orders/OrdersPage";
import BillingQueuePage from "@/pages/BillingQueuePage";
import DdtPage from "@/pages/DdtPage";
import ProformaPage from "@/pages/ProformaPage";
import InvoicesPage from "@/pages/InvoicesPage";
import SupplierInvoicesPage from "@/pages/SupplierInvoicesPage";
import SuppliersPage from "@/pages/SuppliersPage";
import OrderDetail from "@/pages/orders/OrderDetail";
import ContractsPage from "@/pages/contracts/ContractsPage";
import ContractDetail from "@/pages/contracts/ContractDetail";
import { CommercialDocsPage, LicensesPage } from "@/pages/assets/RegistryPages";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import DocumentDetail from "@/pages/documents/DocumentDetail";
import AnalyticsPage from "@/pages/analytics/AnalyticsPage";
import VaultPage from "@/pages/vault/VaultPage";
import { MAIN_MODULES } from "@/lib/modules";

const guarded = (perm, el) => <RequirePermission perm={perm}>{el}</RequirePermission>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/demo" element={<DemoLogin />} />
          <Route path="/accedi/:slug" element={<Login />} />
          <Route path="/firma/:token" element={<SignPage />} />
          <Route path="/modulo-richiesta" element={<WebFormPage />} />
          <Route path="/modulo-tecnico" element={<TechFormPage />} />
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={guarded("dashboard:read", <Dashboard />)} />
            <Route path="/calendario" element={guarded("calendar:read", <CalendarPage />)} />
            <Route path="/clienti" element={guarded("companies:read", <CompaniesList />)} />
            <Route path="/clienti/:recordId" element={guarded("companies:read", <CompanyDetail />)} />
            <Route path="/fornitori/:recordId" element={guarded("companies:read", <CompanyDetail />)} />
            <Route path="/lead" element={guarded("leads:read", <LeadsList />)} />
            <Route path="/lead/:recordId" element={guarded("leads:read", <LeadsList />)} />
            <Route path="/attivita" element={guarded("activities:read", <ActivitiesPage />)} />
            <Route path="/opportunita" element={guarded("opportunities:read", <OpportunitiesPage />)} />
            <Route path="/opportunita/:recordId" element={guarded("opportunities:read", <OpportunityDetail />)} />
            <Route path="/offerte" element={guarded("quotes:read", <QuotesList />)} />
            <Route path="/offerte/:recordId" element={guarded("quotes:read", <QuoteDetail />)} />
            <Route path="/follow-up" element={guarded("opportunities:read", <FollowupPage />)} />
            <Route path="/forecast" element={guarded("opportunities:read", <ForecastPage />)} />
            <Route path="/catalogo" element={guarded("products:read", <CatalogPage />)} />
            <Route path="/admin/pipeline" element={guarded("pipeline:manage", <AdminPipeline />)} />
            <Route path="/interventi" element={guarded("interventions:read", <InterventionsPage />)} />
            <Route path="/interventi/:recordId" element={guarded("interventions:read", <InterventionDetail />)} />
            <Route path="/rapportini" element={guarded("reports:read", <ReportsPage />)} />
            <Route path="/moduli-tecnici" element={guarded("interventions:create", <TechSubmissionsPage />)} />
            <Route path="/rapportini/:recordId" element={guarded("reports:read", <ReportDetail />)} />
            <Route path="/admin/tariffe" element={guarded("rates:read", <RateCardsPage />)} />
            <Route path="/admin/categorie-asset" element={guarded("assets:read", <AssetCategoriesPage />)} />
            <Route path="/prodotti-asset" element={guarded("assets:read", <AssetsPage />)} />
            <Route path="/prodotti-asset/:recordId" element={guarded("assets:read", <AssetDetail />)} />
            <Route path="/licenze" element={guarded("licenses:read", <LicensesPage />)} />
            <Route path="/ordini" element={guarded("ordini:read", <OrdersPage />)} />
            <Route path="/da-fatturare" element={guarded("billing:read", <BillingQueuePage />)} />
            <Route path="/fatture" element={guarded("invoices:read", <InvoicesPage />)} />
            <Route path="/proforma" element={guarded("proforma:read", <ProformaPage />)} />
            <Route path="/ddt" element={guarded("ordini:read", <DdtPage />)} />
            <Route path="/fatture-fornitori" element={guarded("supplier_invoices:read", <SupplierInvoicesPage />)} />
            <Route path="/fornitori" element={guarded("supplier_invoices:read", <SuppliersPage />)} />
            <Route path="/ordini/:recordId" element={guarded("ordini:read", <OrderDetail />)} />
            <Route path="/password-manager" element={<VaultPage />} />
            <Route path="/contratti" element={guarded("contracts:read", <ContractsPage />)} />
            <Route path="/contratti/:recordId" element={guarded("contracts:read", <ContractDetail />)} />
            <Route path="/documenti-commerciali" element={guarded("commercial_docs:read", <CommercialDocsPage />)} />
            <Route path="/documenti" element={guarded("documents:read", <DocumentsPage />)} />
            <Route path="/documenti/:id" element={guarded("documents:read", <DocumentDetail />)} />
            <Route path="/analytics" element={guarded("analytics:read", <AnalyticsPage />)} />
            {MAIN_MODULES.filter((m) => m.phase).flatMap((m) => [
              <Route key={m.id} path={m.path} element={guarded(m.perm, <PlaceholderModule module={m} />)} />,
              <Route key={`${m.id}-rec`} path={`${m.path}/:recordId`} element={guarded(m.perm, <PlaceholderModule module={m} />)} />,
            ])}
            <Route path="/admin/aziende" element={<PlatformTenants />} />
            <Route path="/admin/utenti" element={guarded("users:read", <AdminUsers />)} />
            <Route path="/admin/ruoli" element={guarded("roles:read", <AdminRoles />)} />
            <Route path="/admin/configurazione" element={guarded("config:read", <AdminConfig />)} />
            <Route path="/admin/audit-log" element={guarded("audit:read", <AdminAudit />)} />
            <Route path="/admin/automazioni" element={guarded("automations:read", <AutomationsPage />)} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <QualifyHost />
    </AuthProvider>
  );
}

export default App;
